// features/system/dbops/hooks/useDbops.ts
// ────────────────────────────────────────────────────────────────────────────
// Hook-owns-all-state for the Admin → DB Ops tile.
//
// Owns:
//   • Fetch + cache via fetchData() (handles JWT refresh + 401 retry).
//   • Polling every 30 s (auto-pause when tab is hidden).
//   • Manual `refresh()` action.
//   • Derived freshness label for the data-freshness badge.
//
// Returns:
//   {
//     data:      DbopsState | null,
//     error:     string | null,
//     loading:   boolean,
//     refreshing:boolean,
//     refresh:   () => void,
//     freshness: 'live' | 'cached' | 'stale' | 'offline',
//     lastFetchedAt: number | null,
//   }
//
// Zero-prop discipline: the tile component takes NO props from AdminDashboard;
// it imports this hook directly.
// ────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchData } from '@/utils/fetch';

export type DbopsSeed = {
  seedFile:  string;
  seedEnv:   string;
  sha256:    string;
  appliedAt: string;
  appliedBy: string;
  freshness: 'fresh' | 'recent' | 'aged' | 'unknown';
};

export type DbopsState = {
  schemaVersion: number | null;
  schemaDirty:   boolean | null;
  seeds: DbopsSeed[];
  summary: {
    total: number;
    byEnv: Record<string, number>;
    oldestAppliedAt: string | null;
    newestAppliedAt: string | null;
  };
  serverTime: string;
};

const POLL_MS         = 30_000;
const STALE_AFTER_MS  = 60_000;
const OFFLINE_AFTER_MS = 5 * 60_000;

export function useDbops() {
  const [data,    setData]    = useState<DbopsState | null>(null);
  const [error,   setError]   = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState<number | null>(null);
  // Bump on every poll tick so the freshness badge re-evaluates without
  // requiring a fresh data fetch.
  const [, forceTick] = useState(0);

  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async (kind: 'initial' | 'refresh') => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    if (kind === 'initial') setLoading(true);
    else                    setRefreshing(true);
    setError(null);

    try {
      const json = await fetchData('/bff/admin/dbops/state', { signal: ac.signal });
      setData(json as DbopsState);
      setLastFetchedAt(Date.now());
    } catch (e: any) {
      // AbortError is benign during unmount / re-poll.
      if (e?.name === 'AbortError') return;
      setError(e?.message || 'Failed to fetch dbops state');
    } finally {
      if (kind === 'initial') setLoading(false);
      else                    setRefreshing(false);
    }
  }, []);

  // Initial fetch + interval polling.
  useEffect(() => {
    void load('initial');
    const t = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      void load('refresh');
    }, POLL_MS);
    // Tick once a second so the freshness badge re-renders in real time.
    const tickT = setInterval(() => forceTick(n => (n + 1) % 1_000_000), 1_000);
    return () => {
      clearInterval(t);
      clearInterval(tickT);
      abortRef.current?.abort();
    };
  }, [load]);

  const refresh = useCallback(() => { void load('refresh'); }, [load]);

  // Derive freshness label.
  let freshness: 'live' | 'cached' | 'stale' | 'offline' = 'live';
  if (error) {
    freshness = 'offline';
  } else if (lastFetchedAt) {
    const age = Date.now() - lastFetchedAt;
    if      (age > OFFLINE_AFTER_MS) freshness = 'offline';
    else if (age > STALE_AFTER_MS)   freshness = 'stale';
    else if (age > POLL_MS)          freshness = 'cached';
    else                             freshness = 'live';
  } else if (!loading) {
    freshness = 'offline';
  }

  return { data, error, loading, refreshing, refresh, freshness, lastFetchedAt };
}
